import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { Percentage } from '@ui/components/Amount/Percentage';
import { Pnl } from '@ui/components/Amount/Pnl';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useTradePairInfosByPairBase } from '@ui/hooks/trade/useTradePairInfos';
import { Text } from '@ui/lib/park/ui/text';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { isNil, omit } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, HStack, Stack, VStack } from 'styled-system/jsx';

import { usePositionListContext } from './context';
import { FeeLearnMoreLink } from './FeeLearnMoreLink';
import { FundingFeeDisplay } from './FundingFeeDisplay';
import { valueStyle } from './styles';
import { PositionMarketData } from './types';

export const PnLTooltipContent = ({
  position,
  marketData,
}: {
  position: Position;
  marketData?: PositionMarketData;
}) => {
  if (marketData) {
    return (
      <PnLTooltipContentValue position={position} marketData={marketData} />
    );
  }

  return <PnLTooltipContentWithHooks position={position} />;
};

const PnLTooltipContentWithHooks = ({ position }: { position: Position }) => {
  const pairMarketPrice = usePriceLive(position.pair.baseSymbol);
  const collateralMarketPrice = usePayTokenPriceLatest(
    position.collateralToken,
  );
  const tradePair = useTradePairInfosByPairBase(position.pair.address);

  return (
    <PnLTooltipContentValue
      position={position}
      marketData={{
        pairMarketPrice,
        collateralMarketPrice,
        tradePair,
      }}
    />
  );
};

const PnLTooltipContentValue = ({
  position,
  marketData,
}: {
  position: Position;
  marketData: PositionMarketData;
}) => {
  const { t } = useTranslation();
  const { feeConfig } = usePositionListContext();
  const pairMarketPrice = marketData.pairMarketPrice;
  const collateralMarketPrice = marketData.collateralMarketPrice;
  const tradePair = marketData.tradePair;

  const closePositionFee = useMemo(() => {
    if (!feeConfig || !pairMarketPrice || !collateralMarketPrice || !tradePair)
      return;

    return position.getClosePositionFee({
      ...feeConfig,
      collateralMarketPrice,
      pairMarketPrice: pairMarketPrice,
      tradePair,
    });
  }, [collateralMarketPrice, feeConfig, pairMarketPrice, position, tradePair]);

  const pnlAfterFee = useMemo(() => {
    if (!feeConfig || !pairMarketPrice || !collateralMarketPrice || !tradePair)
      return;

    return {
      value: position.getPnlAfterFee({
        ...feeConfig,
        pairMarketPrice: pairMarketPrice,
        collateralMarketPrice,
        tradePair,
      }),
      percentage: position.getPnlAfterFeePercentage({
        ...feeConfig,
        pairMarketPrice: pairMarketPrice,
        collateralMarketPrice,
        tradePair,
      }),
    };
  }, [collateralMarketPrice, feeConfig, pairMarketPrice, position, tradePair]);

  const shouldHideDiscount = position.pair.isDegenPair;

  return (
    <Stack minW="60" gap={1} maxW="300px">
      <Box
        borderBottom="1px solid"
        borderBottomColor="greyWhite.outline"
        pb={2}
        pt={2}
        textStyle="headings.small"
        color="greyWhite.white"
      >
        <HStack justify="space-between" alignItems="flex-start">
          <Text css={{ ...valueStyle, textStyle: 'headings.xsmall' }}>
            PnL ({t('common.after fees')})
          </Text>
          {!isNil(pnlAfterFee) && (
            <VStack gap={1}>
              <Pnl
                value={pnlAfterFee.value}
                css={{
                  textStyle: 'headings.xsmall',
                }}
                data-cy="PnLToolTip-PnLAfterFee"
              />
              <Percentage
                textStyle="text.small"
                value={pnlAfterFee.percentage}
                opts={{
                  isShowSign: true,
                }}
                data-cy="PnLToolTip-PnLAfterFeePercentage"
                data-cy-value={pnlAfterFee.percentage}
              />
            </VStack>
          )}
        </HStack>
      </Box>
      <HStack mt={4} justify="space-between">
        <Text css={valueStyle}>PnL</Text>
        {!!pairMarketPrice && !!tradePair && (
          <Pnl
            value={position.getPnl({
              pairMarketPrice: pairMarketPrice,
              tradePair,
            })}
            css={omit(valueStyle, 'color')}
            opts={{
              isShowSign: true,
            }}
            data-cy="PnLToolTip-PnL"
          />
        )}
      </HStack>

      <HStack justify="space-between">
        <Text css={valueStyle}>{t('common.Close Fee')}</Text>
        {!!closePositionFee && (
          <HStack gap={1}>
            {!closePositionFee.closeFee.eq(closePositionFee.finalCloseFee) && (
              <AmountUsd
                textDecoration="line-through"
                value={closePositionFee.closeFee.times(-1)}
                css={valueStyle}
                data-cy="PnLToolTip-CloseFee"
                data-cy-value={closePositionFee.closeFee.times(-1)}
              />
            )}
            {closePositionFee.finalCloseFee.isZero() ? (
              <Text css={{ ...valueStyle, color: 'system.successLight' }}>
                {t('common.Free')}
              </Text>
            ) : (
              <AmountUsd
                value={closePositionFee.finalCloseFee.times(-1)}
                css={valueStyle}
                data-cy="PnLToolTip-CloseFee"
                data-cy-value={closePositionFee.finalCloseFee.times(-1)}
              />
            )}
          </HStack>
        )}
      </HStack>

      <HStack justify="space-between">
        <Text css={valueStyle}>{t('common.Funding Fee')}</Text>
        <FundingFeeDisplay
          position={position}
          collateralMarketPrice={collateralMarketPrice}
          data-cy="PnLToolTip-FundingFee"
        />
      </HStack>
    </Stack>
  );
};
